import { Box, CircularProgress } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangeSelector from '../../../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../../../components/FilterSelectorWithApp';
import FilterSelectorUser, {
  type UserFilterValue,
} from '../../../../components/pointhome/roulette/chart/FilterSelectorUser';
import RouletteChartDashboard, {
  ROULETTE_CONTENT_OPTIONS,
  TIMING_FILTER_OPTIONS,
} from '../../../../components/pointhome/roulette/chart/RouletteChartDashboard';
import type { RouletteKpiCardData } from '../../../../components/pointhome/roulette/chart/RouletteKpiCard';
import type { ConversionStep } from '../../../../components/pointhome/roulette/chart/RouletteConversionChart';
import type { RouletteHomeVisitRow } from '../../../../components/pointhome/roulette/chart/table/RouletteHomeVisitTable';
import type { RouletteVideoAdRow } from '../../../../components/pointhome/roulette/chart/table/RouletteVideoAdTable';
import type { RouletteRandomAdRow } from '../../../../components/pointhome/roulette/chart/table/RouletteRandomAdTable';
import type { RouletteTubeFishingRow } from '../../../../components/pointhome/roulette/chart/table/RouletteTubeFishingTable';
import type { RouletteConversionStepsRow } from '../../../../components/pointhome/roulette/chart/table/RouletteConversionStepsTable';
import { getConversionDataForContent } from '../../../../components/pointhome/roulette/chart/rouletteContentSteps';
import apiClient from '../../../../utils/axiosClient';

function computeKpiFromConversionData(steps: ConversionStep[]): RouletteKpiCardData {
  if (!steps.length) {
    return {
      totalEventCount: 0,
      finalConversionRate: 0,
      exitSectionFrom: 0,
      exitSectionTo: 0,
      exitSectionRate: 0,
    };
  }
  const firstCount = steps[0].count;
  const lastCount = steps[steps.length - 1].count;
  const totalEventCount = firstCount;
  const finalConversionRate = firstCount > 0 ? (lastCount / firstCount) * 100 : 0;

  let exitSectionFrom = 0;
  let exitSectionTo = 0;
  let exitSectionRate = 0;
  if (steps.length >= 2) {
    let maxDrop = 0;
    for (let i = 0; i < steps.length - 1; i++) {
      const rateI = firstCount > 0 ? (steps[i].count / firstCount) * 100 : 0;
      const rateI1 = firstCount > 0 ? (steps[i + 1].count / firstCount) * 100 : 0;
      const drop = rateI1 - rateI;
      if (drop < maxDrop) {
        maxDrop = drop;
        exitSectionFrom = i + 1;
        exitSectionTo = i + 2;
        exitSectionRate = drop;
      }
    }
  }
  return {
    totalEventCount,
    finalConversionRate,
    exitSectionFrom,
    exitSectionTo,
    exitSectionRate,
  };
}

type TimingFishingResult = {
  event?: string;
  eventLabel?: string;
  count?: number;
  eventCount?: number;
};
type TimingFishingDailyStat = { date: string; results: TimingFishingResult[] };
type TimingFishingApiResponse = {
  dailyStats: TimingFishingDailyStat[];
  totalStats: TimingFishingResult[];
};

function aggregateTimingResultsByEvent(results: TimingFishingResult[]): Record<string, number> {
  const list = Array.isArray(results) ? results : [];
  return list.reduce<Record<string, number>>((acc, cur) => {
    const eventKey = String(cur.event ?? cur.eventLabel ?? '').trim();
    const c = Number(cur.count ?? cur.eventCount) || 0;
    if (eventKey) acc[eventKey] = (acc[eventKey] ?? 0) + c;
    return acc;
  }, {});
}

/** 뜰채 낚시: event 순서 기준으로 전환 데이터 계산 */
function buildConversionFromEventOrder(
  totalStats: TimingFishingResult[],
  order: { label: string; event: string }[],
): ConversionStep[] {
  const totalMap = aggregateTimingResultsByEvent(totalStats);
  return order.map(({ label, event }, index) => {
    const count = totalMap[event] ?? 0;
    if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
    const prevEvent = order[index - 1]?.event;
    const prevCount = prevEvent ? (totalMap[prevEvent] ?? 0) : 0;
    return { label, percentage: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0), count };
  });
}

/** 뜰채 낚시: event 순서 기준으로 일자별 rows 계산 */
function buildRowsFromEventOrder<
  TRow extends {
    id: number;
    date: string;
    totalCount: number;
    totalRate: number;
    steps: { count: number; rate: number }[];
  },
>(dailyStats: TimingFishingDailyStat[], order: { event: string }[]): TRow[] {
  return dailyStats.map((daily, index) => {
    const eventMap = aggregateTimingResultsByEvent(daily.results);
    const firstEvent = order[0]?.event;
    const firstDailyCount = firstEvent ? (eventMap[firstEvent] ?? 0) : 0;
    const lastEvent = order[order.length - 1]?.event;
    const lastCount = lastEvent ? (eventMap[lastEvent] ?? 0) : 0;
    const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);
    const steps = order.map(({ event }, stepIndex) => {
      const count = eventMap[event] ?? 0;
      if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
      const prevEvent = order[stepIndex - 1]?.event;
      const prevCount = prevEvent ? (eventMap[prevEvent] ?? 0) : 0;
      return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
    });
    return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps } as TRow;
  });
}

/** 뜰채 낚시: 스텝 수는 필터와 무관하게 항상 9 - 마지막 스텝(결과 화면 진입)만 bonus_ticket 속성으로 구분 */
const SCOOP_EVENT_ORDER: { label: string; event: string }[] = [
  { label: '게임 페이지 진입', event: 'scoop_gamepage_view' },
  { label: '게임 시작하기 클릭', event: 'scoop_start_click' },
  { label: '인트로 화면 클릭', event: 'scoop_intro_click' },
  { label: '가이드1 화면 클릭', event: 'scoop_guide1_click' },
  { label: '가이드2 화면 클릭', event: 'scoop_guide2_click' },
  { label: '잡기 버튼 클릭', event: 'scoop_catch_click' },
  { label: '광고 유도 페이지 진입', event: 'scoop_adpage_view' },
  { label: '결과 보고 티켓 받기 클릭', event: 'scoop_ticket_click' },
  { label: '결과 화면 진입', event: 'scoop_resultpage_view' },
];

function buildEmptyScoopConversionData(): ConversionStep[] {
  return SCOOP_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

type HomevisitDailyStat = {
  date: string;
  results: {
    eventLabel: string;
    eventCount: number;
  }[];
};

type HomevisitTotalStat = {
  eventLabel: string;
  eventCount: number;
};

type HomevisitApiResponse = {
  dailyStats: HomevisitDailyStat[];
  totalStats: HomevisitTotalStat[];
};

const HOMEVISIT_EVENT_ORDER: {
  label: string;
  eventLabel: string;
}[] = [
  // 이미지 및 백엔드 정의 기준 순서
  { label: '참여하기 클릭', eventLabel: 'homevisit_mainbutton_click' },
  { label: '광고 보고 보상받기 클릭', eventLabel: 'homevisit_btsheetbutton_click' },
  { label: '광고 클릭', eventLabel: 'homevisit_ad_click' },
  { label: '티켓 받기 클릭', eventLabel: 'homevisit_ticket_click' },
  { label: '티켓 획득 완료', eventLabel: 'homevisit_ad_endpage' },
];

/** 첫 방문 보상: 온보딩 가이드 ~ 웰컴 보상 6단계 (API event/popup_type ↔ 차트/테이블 라벨) */
const FIRST_VISIT_EVENT_ORDER: { label: string; event: string; popupType?: string }[] = [
  { label: '온보딩 가이드 노출', event: 'roulette__guide__view' },
  { label: '시작하기 클릭', event: 'roulette__start__click' },
  { label: '약관 동의 바텀시트 노출', event: 'roulette__tnc__view' },
  { label: '동의하고 시작하기 클릭', event: 'roulette__tnc__click' },
  { label: '첫 방문 보상 획득', event: 'welcome__popup__view', popupType: 'attendance' },
  { label: '미션 보상 획득', event: 'welcome__popup__view', popupType: 'mission_reward' },
];

/** event(+popup_type) 조합 키. popup_type이 없으면 event 단독으로 매칭 */
function firstVisitStatKey(event: string, popupType?: string): string {
  return popupType ? `${event}|${popupType}` : event;
}

type FirstVisitStatResult = {
  event: string;
  count: number;
  popup_type?: string;
};

type FirstVisitDailyStat = {
  date: string;
  results: FirstVisitStatResult[];
};

type FirstVisitApiResponse = {
  dailyStats: FirstVisitDailyStat[];
  totalStats: FirstVisitStatResult[];
};

function mapUserFilterToUserType(filter: UserFilterValue): string | undefined {
  if (filter === '전체') return 'all';
  if (filter === '기존 사용자') return 'remained';
  if (filter === '신규 사용자') return 'new';
  return undefined;
}

/** 전환율 소수 둘째자리에서 반올림 */
function roundRate(value: number): number {
  return Math.round(value * 100) / 100;
}

function buildEmptyHomevisitConversionData(): ConversionStep[] {
  return HOMEVISIT_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

const LONGAD_EVENT_ORDER: {
  label: string;
  eventLabel: string;
}[] = [
  // 영상 광고 보기 스텝 순서
  { label: '참여하기 클릭', eventLabel: 'longad_mainbutton_click' },
  { label: '티켓 받기 클릭 (1차)', eventLabel: 'longad_ad_click' },
  { label: '티켓 더 받기 클릭', eventLabel: 'longad_ticket1_click' },
  { label: '티켓 받기 클릭 (2차)', eventLabel: 'longad_ticket2_click' },
];

function buildEmptyVideoAdConversionData(): ConversionStep[] {
  return LONGAD_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

const SHORTAD_EVENT_ORDER: {
  label: string;
  eventLabel: string;
}[] = [
  // 랜덤 광고 보기 스텝 순서
  { label: '참여하기 클릭', eventLabel: 'shortad_mainbutton_click' },
  { label: '광고 클릭', eventLabel: 'shortad_ad_click' },
  { label: '티켓 받기 클릭 (1차)', eventLabel: 'shortad_ticket1_click' },
  { label: '한 장 더 받기 클릭', eventLabel: 'shortad_moreticket_click' },
  { label: '티켓 받기 클릭 (2차)', eventLabel: 'shortad_ticket2_click' },
];

function buildEmptyRandomAdConversionData(): ConversionStep[] {
  return SHORTAD_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

type AdticketResult = {
  event?: string;
  count?: number;
  ad_order?: string;
  screen_type?: string;
};

type AdticketDailyStat = {
  date: string;
  results: AdticketResult[];
};

type AdticketApiResponse = {
  dailyStats: AdticketDailyStat[];
  totalStats: AdticketResult[];
};

/** event명만으로 구분 안 되는 adticket 이벤트를 event+ad_order+screen_type 조합으로 키 생성 */
function adticketResultKey(r: { event?: string; ad_order?: string; screen_type?: string }): string {
  return JSON.stringify([r.event ?? '', r.ad_order ?? '', r.screen_type ?? '']);
}

function aggregateAdticketResultsByKey(results: AdticketResult[]): Record<string, number> {
  const list = Array.isArray(results) ? results : [];
  return list.reduce<Record<string, number>>((acc, cur) => {
    const key = adticketResultKey(cur);
    const c = Number(cur.count) || 0;
    acc[key] = (acc[key] ?? 0) + c;
    return acc;
  }, {});
}

const ADTICKET_EVENT_ORDER: {
  label: string;
  event: string;
  ad_order?: string;
  screen_type?: string;
}[] = [
  // 광고보고 티켓 얻기 스텝 순서
  { label: '참여하기', event: 'adticket__participate__click' },
  { label: '1차 광고 클릭', event: 'adticket__ad__click', ad_order: '1' },
  {
    label: '1차 티켓 받기',
    event: 'adticket__cta__click',
    screen_type: 'ticket_claim',
    ad_order: '1',
  },
  { label: '한 장 더 받기', event: 'adticket__cta__click', screen_type: 'extra_available' },
  { label: '2차 광고 클릭', event: 'adticket__ad__click', ad_order: '2' },
  {
    label: '2차 티켓 받기',
    event: 'adticket__cta__click',
    screen_type: 'ticket_claim',
    ad_order: '2',
  },
];

function buildEmptyAdticketConversionData(): ConversionStep[] {
  return ADTICKET_EVENT_ORDER.map(({ label }) => ({ label, percentage: 0, count: 0 }));
}

const REPAIR_EVENT_ORDER: {
  label: string;
  eventLabel: string;
}[] = [
  // 통발 수리 스텝 순서
  { label: '광고 유도 페이지 진입', eventLabel: 'repair_adpage_view' },
  { label: '광고보고 통발 점검하기 클릭 1차', eventLabel: 'repair_ticket1_click' },
  { label: '결과 화면 진입 1차', eventLabel: 'repair_resultpage1_view' },
  { label: '광고보고 통발 점검하기 2차', eventLabel: 'repair_ticket2_click' },
  { label: '결과 화면 진입 2차', eventLabel: 'repair_resultpage2_view' },
];

function buildEmptyRepairConversionData(): ConversionStep[] {
  return REPAIR_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

const PULLUP_EVENT_ORDER: {
  label: string;
  eventLabel: string;
}[] = [
  // 통발 건지기 스텝 순서
  { label: '광고 유도 페이지 진입', eventLabel: 'pullup_adpage_view' },
  { label: '광고보고 통발 건지기 클릭 1차', eventLabel: 'pullup_ticket1_click' },
  { label: '결과 화면 진입 1차', eventLabel: 'pullup_resultpage1_view' },
  { label: '광고보고 통발 건지기 2차', eventLabel: 'pullup_ticket2_click' },
  { label: '결과 화면 진입 2차', eventLabel: 'pullup_resultpage2_view' },
];

function buildEmptyPullupConversionData(): ConversionStep[] {
  return PULLUP_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

const FORTUNE_EVENT_ORDER: {
  label: string;
  eventLabel: string;
}[] = [
  // 낚시운세 스텝 순서
  { label: '광고 유도 페이지 진입', eventLabel: 'fortune_adpage_view' },
  { label: '광고보고 운세 확인하기 클릭', eventLabel: 'fortune_ticket_click' },
  { label: '결과 화면 진입', eventLabel: 'fortune_resultpage_view' },
];

function buildEmptyFortuneConversionData(): ConversionStep[] {
  return FORTUNE_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

/** 튜브 낚시 API: results 항목은 event(eventLabel), count(eventCount), 선택적 tube/ticket */
type TubeFishingResult = {
  event?: string;
  eventLabel?: string;
  count?: number;
  eventCount?: number;
  tube?: string;
  ticket?: string;
};

type TubeFishingDailyStat = {
  date: string;
  results: TubeFishingResult[];
};

type TubeFishingApiResponse = {
  dailyStats: TubeFishingDailyStat[];
  totalStats: TubeFishingResult[];
};

/**
 * 이벤트명 기준으로 count 합산
 * "선택 안함"일 때: totalStats에 튜브별 row(예: tube_choice 42+40+83+26+69=260)가 오므로
 * 이 합산으로 "튜브 선택" 등은 모든 튜브에 대한 카운트 합이 됨
 */
function aggregateTubeResultsByEvent(results: TubeFishingResult[]): Record<string, number> {
  const list = Array.isArray(results) ? results : [];
  return list.reduce<Record<string, number>>((acc, cur) => {
    const eventKey = String(cur.event ?? cur.eventLabel ?? '').trim();
    const c = Number(cur.count ?? cur.eventCount) || 0;
    if (eventKey) {
      acc[eventKey] = (acc[eventKey] ?? 0) + c;
    }
    return acc;
  }, {});
}

const TUBE_EVENT_ORDER: { label: string; event: string }[] = [
  { label: '튜브 낚시\n게임 페이지 진입', event: 'tube_gamepage' },
  { label: '시작하기 클릭', event: 'tube_start_click' },
  { label: '인트로 클릭', event: 'tube_intro_click' },
  { label: '가이드 클릭', event: 'tube_guide_click' },
  { label: '튜브 선택', event: 'tube_choice' },
  { label: '광고\n유도 페이지\n진입', event: 'tube_adpage' },
  { label: '결과 보고\n티켓 받기 클릭', event: 'tube_result_click' },
  { label: '배너 광고 클릭', event: 'tube_ad_click' },
  { label: '다른 게임하고\n티켓 더 받기 클릭', event: 'tube_moregame_click' },
];

function mapTubeFilterToApi(tubeFilter: string): string {
  const map: Record<string, string> = {
    '선택 안함': 'all',
    '도넛 튜브': 'donut',
    '수박 튜브': 'watermelon',
    '오렌지 튜브': 'orange',
    '키위 튜브': 'kiwi',
    '파인애플 튜브': 'pineapple',
  };
  return map[tubeFilter] ?? 'all';
}

/** API tube 코드 → 툴팁 표시명 */
const TUBE_CODE_TO_LABEL: Record<string, string> = {
  donut: '도넛',
  watermelon: '수박',
  orange: '오렌지',
  kiwi: '키위',
  pineapple: '파인애플',
};

/** rawTotal에서 tube_choice 이벤트만 모아 튜브별 비율 계산 (튜브 선택 툴팁용) */
function computeTubeChoicePercentages(rawTotal: TubeFishingResult[]): Record<string, number> {
  const tubeChoiceRows = rawTotal.filter(
    (r) => String(r.event ?? r.eventLabel ?? '').trim() === 'tube_choice',
  );
  const byTube: Record<string, number> = {};
  for (const r of tubeChoiceRows) {
    const tube = String(r.tube ?? '').trim();
    if (!tube) continue;
    const label = TUBE_CODE_TO_LABEL[tube] ?? tube;
    const count = Number(r.count ?? r.eventCount) || 0;
    byTube[label] = (byTube[label] ?? 0) + count;
  }
  const total = Object.values(byTube).reduce((a, b) => a + b, 0);
  if (total === 0) return {};
  const out: Record<string, number> = {};
  for (const [label, count] of Object.entries(byTube)) {
    out[label] = Math.round((count / total) * 10000) / 100;
  }
  return out;
}

/** rawTotal에서 tube_moregame_click 이벤트만 모아 ticket별 비율 계산 (❾ 티켓 더 받기 툴팁용) */
function computeMoreGameTicketPercentages(rawTotal: TubeFishingResult[]): Record<string, number> {
  const rows = rawTotal.filter(
    (r) => String(r.event ?? r.eventLabel ?? '').trim() === 'tube_moregame_click',
  );
  const byTicket: Record<string, number> = {};
  for (const r of rows) {
    const ticket = String(r.ticket ?? '').trim() || '0';
    const count = Number(r.count ?? r.eventCount) || 0;
    byTicket[ticket] = (byTicket[ticket] ?? 0) + count;
  }
  const total = Object.values(byTicket).reduce((a, b) => a + b, 0);
  if (total === 0) return {};
  const out: Record<string, number> = {};
  for (const [ticket, count] of Object.entries(byTicket)) {
    out[ticket] = Math.round((count / total) * 10000) / 100;
  }
  return out;
}

function buildEmptyTubeConversionData(): ConversionStep[] {
  return TUBE_EVENT_ORDER.map(({ label }) => ({
    label,
    percentage: 0,
    count: 0,
  }));
}

export default function RouletteChart() {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(6, 'day').startOf('day'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().startOf('day'));
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [userFilter, setUserFilter] = useState<UserFilterValue>('전체');
  const [contentAnchorEl, setContentAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContent, setSelectedContent] = useState<string>(ROULETTE_CONTENT_OPTIONS[0]);
  const [showPeriodCompare, setShowPeriodCompare] = useState(false);
  const [periodCompareStartDate, setPeriodCompareStartDate] = useState<Dayjs | null>(null);
  const [periodCompareEndDate, setPeriodCompareEndDate] = useState<Dayjs | null>(null);
  const [tubeFilter, setTubeFilter] = useState<string>('선택 안함');
  const [timingFilter, setTimingFilter] = useState<string>(TIMING_FILTER_OPTIONS[0]);
  const [homevisitConversionData, setHomevisitConversionData] = useState<ConversionStep[]>(
    buildEmptyHomevisitConversionData(),
  );
  const [homevisitRows, setHomevisitRows] = useState<RouletteHomeVisitRow[]>([]);
  const [videoAdConversionData, setVideoAdConversionData] = useState<ConversionStep[]>(
    buildEmptyVideoAdConversionData(),
  );
  const [videoAdRows, setVideoAdRows] = useState<RouletteVideoAdRow[]>([]);
  const [randomAdConversionData, setRandomAdConversionData] = useState<ConversionStep[]>(
    buildEmptyRandomAdConversionData(),
  );
  const [randomAdRows, setRandomAdRows] = useState<RouletteRandomAdRow[]>([]);
  const [adticketConversionData, setAdticketConversionData] = useState<ConversionStep[]>(
    buildEmptyAdticketConversionData(),
  );
  const [adticketRows, setAdticketRows] = useState<RouletteConversionStepsRow[]>([]);
  const [tubeConversionData, setTubeConversionData] = useState<ConversionStep[]>(
    buildEmptyTubeConversionData(),
  );
  const [tubeRows, setTubeRows] = useState<RouletteTubeFishingRow[]>([]);
  const [tubeChoicePercentages, setTubeChoicePercentages] = useState<Record<string, number> | null>(
    null,
  );
  const [moreGameTicketPercentages, setMoreGameTicketPercentages] = useState<Record<
    string,
    number
  > | null>(null);
  const [scoopConversionData, setScoopConversionData] = useState<ConversionStep[]>(
    buildEmptyScoopConversionData(),
  );
  const [scoopRows, setScoopRows] = useState<RouletteConversionStepsRow[]>([]);
  const [repairConversionData, setRepairConversionData] = useState<ConversionStep[]>(
    buildEmptyRepairConversionData(),
  );
  const [repairRows, setRepairRows] = useState<RouletteConversionStepsRow[]>([]);
  const [pullupConversionData, setPullupConversionData] = useState<ConversionStep[]>(
    buildEmptyPullupConversionData(),
  );
  const [pullupRows, setPullupRows] = useState<RouletteConversionStepsRow[]>([]);
  const [fortuneConversionData, setFortuneConversionData] = useState<ConversionStep[]>(
    buildEmptyFortuneConversionData(),
  );
  const [fortuneRows, setFortuneRows] = useState<RouletteConversionStepsRow[]>([]);
  const [firstVisitApiData, setFirstVisitApiData] = useState<FirstVisitApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  /** 기간 비교 시 비교 기간 API 결과 (현재 선택된 콘텐츠 기준) */
  const [compareConversionData, setCompareConversionData] = useState<ConversionStep[] | null>(null);
  /** 튜브 낚시 기간 비교 시 비교 기간의 튜브별 선택 비율 / 티켓별 비율 (툴팁용) */
  const [tubeCompareChoicePercentages, setTubeCompareChoicePercentages] = useState<Record<
    string,
    number
  > | null>(null);
  const [moreGameTicketComparePercentages, setMoreGameTicketComparePercentages] = useState<Record<
    string,
    number
  > | null>(null);
  const [homevisitCompareRows, setHomevisitCompareRows] = useState<RouletteHomeVisitRow[]>([]);
  const [videoAdCompareRows, setVideoAdCompareRows] = useState<RouletteVideoAdRow[]>([]);
  const [randomAdCompareRows, setRandomAdCompareRows] = useState<RouletteRandomAdRow[]>([]);
  const [adticketCompareRows, setAdticketCompareRows] = useState<RouletteConversionStepsRow[]>([]);
  const [tubeCompareRows, setTubeCompareRows] = useState<RouletteTubeFishingRow[]>([]);
  const [scoopCompareRows, setScoopCompareRows] = useState<RouletteConversionStepsRow[]>([]);
  const [repairCompareRows, setRepairCompareRows] = useState<RouletteConversionStepsRow[]>([]);
  const [pullupCompareRows, setPullupCompareRows] = useState<RouletteConversionStepsRow[]>([]);
  const [fortuneCompareRows, setFortuneCompareRows] = useState<RouletteConversionStepsRow[]>([]);
  const [firstVisitCompareRows, setFirstVisitCompareRows] = useState<RouletteConversionStepsRow[]>(
    [],
  );

  const firstVisitConversionData = useMemo(() => {
    const data = firstVisitApiData;
    const order = FIRST_VISIT_EVENT_ORDER;
    if (!data) {
      return order.map(({ label }) => ({ label, percentage: 0, count: 0 }));
    }
    const totalMap = data.totalStats.reduce<Record<string, number>>((acc, cur) => {
      acc[firstVisitStatKey(cur.event, cur.popup_type)] = cur.count;
      return acc;
    }, {});
    return order.map(({ label, event, popupType }, index) => {
      const count = totalMap[firstVisitStatKey(event, popupType)] ?? 0;
      if (index === 0) {
        return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
      }
      const prev = order[index - 1];
      const prevCount = prev ? (totalMap[firstVisitStatKey(prev.event, prev.popupType)] ?? 0) : 0;
      const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
      return { label, percentage, count };
    });
  }, [firstVisitApiData]);

  const firstVisitRows = useMemo((): RouletteConversionStepsRow[] => {
    const data = firstVisitApiData;
    const order = FIRST_VISIT_EVENT_ORDER;
    if (!data?.dailyStats?.length) return [];
    return data.dailyStats.map((daily, index) => {
      const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
        acc[firstVisitStatKey(cur.event, cur.popup_type)] = cur.count;
        return acc;
      }, {});
      const first = order[0];
      const firstDailyCount = first
        ? (eventMap[firstVisitStatKey(first.event, first.popupType)] ?? 0)
        : 0;
      const last = order[order.length - 1];
      const lastCount = last ? (eventMap[firstVisitStatKey(last.event, last.popupType)] ?? 0) : 0;
      const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);
      const steps = order.map(({ event, popupType }, stepIndex) => {
        const count = eventMap[firstVisitStatKey(event, popupType)] ?? 0;
        if (stepIndex === 0) {
          return { count, rate: roundRate(count > 0 ? 100 : 0) };
        }
        const prev = order[stepIndex - 1];
        const prevCount = prev ? (eventMap[firstVisitStatKey(prev.event, prev.popupType)] ?? 0) : 0;
        return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
      });
      return {
        id: index + 1,
        date: daily.date,
        totalCount: lastCount,
        totalRate,
        steps,
      };
    });
  }, [firstVisitApiData]);

  const conversionDataForKpi = useMemo(() => {
    if (selectedContent === '홈 방문하기') return homevisitConversionData;
    if (selectedContent === '영상 광고 보기') return videoAdConversionData;
    if (selectedContent === '랜덤 광고 보기') return randomAdConversionData;
    if (selectedContent === '광고보고 티켓 얻기') return adticketConversionData;
    if (selectedContent === '튜브 낚시') return tubeConversionData;
    if (selectedContent === '뜰채 낚시') return scoopConversionData;
    if (selectedContent === '통발 수리') return repairConversionData;
    if (selectedContent === '통발 건지기') return pullupConversionData;
    if (selectedContent === '낚시운세') return fortuneConversionData;
    if (selectedContent === '첫 방문 보상') return firstVisitConversionData;
    return getConversionDataForContent(selectedContent);
  }, [
    selectedContent,
    homevisitConversionData,
    videoAdConversionData,
    randomAdConversionData,
    adticketConversionData,
    tubeConversionData,
    scoopConversionData,
    repairConversionData,
    pullupConversionData,
    fortuneConversionData,
    firstVisitConversionData,
  ]);

  const kpiData = useMemo(
    () => computeKpiFromConversionData(conversionDataForKpi),
    [conversionDataForKpi],
  );

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      })
      .catch(() => undefined);
  }, []);

  const fetchHomevisitData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<HomevisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/homevisit`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;

      const totalMap = totalStats.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.eventLabel] = cur.eventCount;
        return acc;
      }, {});

      const newConversionData: ConversionStep[] = HOMEVISIT_EVENT_ORDER.map(
        ({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;

          // 1번 스텝은 기준 100%, 이후 스텝은 직전 스텝 대비 전환율
          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEventLabel = HOMEVISIT_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (totalMap[prevEventLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteHomeVisitRow[] = dailyStats.map((daily, index) => {
        const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});

        const firstEventLabel = HOMEVISIT_EVENT_ORDER[0]?.eventLabel;
        const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
        const lastEventLabel = HOMEVISIT_EVENT_ORDER[HOMEVISIT_EVENT_ORDER.length - 1]?.eventLabel;
        const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = HOMEVISIT_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
          const count = eventMap[eventLabel] ?? 0;

          // 1번 스텝은 기준 100%, 이후 스텝은 직전 스텝 대비 전환율
          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEventLabel = HOMEVISIT_EVENT_ORDER[stepIndex - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setHomevisitConversionData(newConversionData);
      setHomevisitRows(newRows);
    } catch {
      setHomevisitConversionData(buildEmptyHomevisitConversionData());
      setHomevisitRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  const fetchVideoAdData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<HomevisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/longad`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;

      const totalMap = totalStats.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.eventLabel] = cur.eventCount;
        return acc;
      }, {});

      const newConversionData: ConversionStep[] = LONGAD_EVENT_ORDER.map(
        ({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;

          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEventLabel = LONGAD_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (totalMap[prevEventLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteVideoAdRow[] = dailyStats.map((daily, index) => {
        const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});

        const firstEventLabel = LONGAD_EVENT_ORDER[0]?.eventLabel;
        const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
        const lastEventLabel = LONGAD_EVENT_ORDER[LONGAD_EVENT_ORDER.length - 1]?.eventLabel;
        const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = LONGAD_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
          const count = eventMap[eventLabel] ?? 0;

          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEventLabel = LONGAD_EVENT_ORDER[stepIndex - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setVideoAdConversionData(newConversionData);
      setVideoAdRows(newRows);
    } catch {
      setVideoAdConversionData(buildEmptyVideoAdConversionData());
      setVideoAdRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  const fetchRandomAdData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<HomevisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/shortad`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;

      const totalMap = totalStats.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.eventLabel] = cur.eventCount;
        return acc;
      }, {});

      const newConversionData: ConversionStep[] = SHORTAD_EVENT_ORDER.map(
        ({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;

          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEventLabel = SHORTAD_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (totalMap[prevEventLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteRandomAdRow[] = dailyStats.map((daily, index) => {
        const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});

        const firstEventLabel = SHORTAD_EVENT_ORDER[0]?.eventLabel;
        const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
        const lastEventLabel = SHORTAD_EVENT_ORDER[SHORTAD_EVENT_ORDER.length - 1]?.eventLabel;
        const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = SHORTAD_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
          const count = eventMap[eventLabel] ?? 0;

          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEventLabel = SHORTAD_EVENT_ORDER[stepIndex - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setRandomAdConversionData(newConversionData);
      setRandomAdRows(newRows);
    } catch {
      setRandomAdConversionData(buildEmptyRandomAdConversionData());
      setRandomAdRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  const fetchAdticketData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<AdticketApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/adticket`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;
      const totalMap = aggregateAdticketResultsByKey(totalStats ?? []);

      const newConversionData: ConversionStep[] = ADTICKET_EVENT_ORDER.map((step, index) => {
        const count = totalMap[adticketResultKey(step)] ?? 0;

        if (index === 0) {
          return { label: step.label, percentage: roundRate(count > 0 ? 100 : 0), count };
        }

        const prevCount = totalMap[adticketResultKey(ADTICKET_EVENT_ORDER[index - 1])] ?? 0;
        const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
        return { label: step.label, percentage, count };
      });

      const newRows: RouletteConversionStepsRow[] = (dailyStats ?? []).map((daily, index) => {
        const eventMap = aggregateAdticketResultsByKey(daily.results);

        const firstCount = eventMap[adticketResultKey(ADTICKET_EVENT_ORDER[0])] ?? 0;
        const lastKey = adticketResultKey(ADTICKET_EVENT_ORDER[ADTICKET_EVENT_ORDER.length - 1]);
        const lastCount = eventMap[lastKey] ?? 0;
        const totalRate = roundRate(firstCount > 0 ? (lastCount / firstCount) * 100 : 0);

        const steps = ADTICKET_EVENT_ORDER.map((step, stepIndex) => {
          const count = eventMap[adticketResultKey(step)] ?? 0;
          if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
          const prevCount = eventMap[adticketResultKey(ADTICKET_EVENT_ORDER[stepIndex - 1])] ?? 0;
          return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
        });

        return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
      });

      setAdticketConversionData(newConversionData);
      setAdticketRows(newRows);
    } catch {
      setAdticketConversionData(buildEmptyAdticketConversionData());
      setAdticketRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  const fetchRepairData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<HomevisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/repair`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;

      const totalMap = totalStats.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.eventLabel] = cur.eventCount;
        return acc;
      }, {});

      const newConversionData: ConversionStep[] = REPAIR_EVENT_ORDER.map(
        ({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;

          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEventLabel = REPAIR_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (totalMap[prevEventLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
        const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});

        const firstEventLabel = REPAIR_EVENT_ORDER[0]?.eventLabel;
        const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
        const lastEventLabel = REPAIR_EVENT_ORDER[REPAIR_EVENT_ORDER.length - 1]?.eventLabel;
        const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = REPAIR_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
          const count = eventMap[eventLabel] ?? 0;

          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEventLabel = REPAIR_EVENT_ORDER[stepIndex - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setRepairConversionData(newConversionData);
      setRepairRows(newRows);
    } catch {
      setRepairConversionData(buildEmptyRepairConversionData());
      setRepairRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  const fetchPullupData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<HomevisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/pullup`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;

      const totalMap = totalStats.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.eventLabel] = cur.eventCount;
        return acc;
      }, {});

      const newConversionData: ConversionStep[] = PULLUP_EVENT_ORDER.map(
        ({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;

          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEventLabel = PULLUP_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (totalMap[prevEventLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
        const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});

        const firstEventLabel = PULLUP_EVENT_ORDER[0]?.eventLabel;
        const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
        const lastEventLabel = PULLUP_EVENT_ORDER[PULLUP_EVENT_ORDER.length - 1]?.eventLabel;
        const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = PULLUP_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
          const count = eventMap[eventLabel] ?? 0;

          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEventLabel = PULLUP_EVENT_ORDER[stepIndex - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setPullupConversionData(newConversionData);
      setPullupRows(newRows);
    } catch {
      setPullupConversionData(buildEmptyPullupConversionData());
      setPullupRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  const fetchFortuneData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<HomevisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/fortune`,
        { params },
      );

      const { dailyStats, totalStats } = response.data;

      const totalMap = totalStats.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.eventLabel] = cur.eventCount;
        return acc;
      }, {});

      const newConversionData: ConversionStep[] = FORTUNE_EVENT_ORDER.map(
        ({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;

          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEventLabel = FORTUNE_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (totalMap[prevEventLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
        const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});

        const firstEventLabel = FORTUNE_EVENT_ORDER[0]?.eventLabel;
        const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
        const lastEventLabel = FORTUNE_EVENT_ORDER[FORTUNE_EVENT_ORDER.length - 1]?.eventLabel;
        const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = FORTUNE_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
          const count = eventMap[eventLabel] ?? 0;

          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEventLabel = FORTUNE_EVENT_ORDER[stepIndex - 1]?.eventLabel;
          const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setFortuneConversionData(newConversionData);
      setFortuneRows(newRows);
    } catch {
      setFortuneConversionData(buildEmptyFortuneConversionData());
      setFortuneRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (selectedContent === '홈 방문하기') {
      fetchHomevisitData();
    }
  }, [selectedContent, fetchHomevisitData]);

  useEffect(() => {
    if (selectedContent === '영상 광고 보기') {
      fetchVideoAdData();
    }
  }, [selectedContent, fetchVideoAdData]);

  useEffect(() => {
    if (selectedContent === '랜덤 광고 보기') {
      fetchRandomAdData();
    }
  }, [selectedContent, fetchRandomAdData]);

  useEffect(() => {
    if (selectedContent === '광고보고 티켓 얻기') {
      fetchAdticketData();
    }
  }, [selectedContent, fetchAdticketData]);

  useEffect(() => {
    if (selectedContent === '통발 수리') {
      fetchRepairData();
    }
  }, [selectedContent, fetchRepairData]);

  useEffect(() => {
    if (selectedContent === '통발 건지기') {
      fetchPullupData();
    }
  }, [selectedContent, fetchPullupData]);

  useEffect(() => {
    if (selectedContent === '낚시운세') {
      fetchFortuneData();
    }
  }, [selectedContent, fetchFortuneData]);

  const fetchScoopData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const isBonusWon = timingFilter === '보너스 티켓 당첨';

    const params: Record<string, string | boolean> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      bonus_type: isBonusWon,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<TimingFishingApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/scoop`,
        { params },
      );
      const data = response.data ?? {};
      const dailyStats: TimingFishingDailyStat[] = data.dailyStats ?? [];
      const rawTotal = Array.isArray(data.totalStats) ? data.totalStats : [];

      const newConversionData = buildConversionFromEventOrder(rawTotal, SCOOP_EVENT_ORDER);
      const newRows = buildRowsFromEventOrder<RouletteConversionStepsRow>(
        dailyStats,
        SCOOP_EVENT_ORDER,
      );

      setScoopConversionData(newConversionData);
      setScoopRows(newRows);
    } catch {
      setScoopConversionData(buildEmptyScoopConversionData());
      setScoopRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter, timingFilter]);

  useEffect(() => {
    if (selectedContent === '뜰채 낚시') {
      fetchScoopData();
    }
  }, [selectedContent, timingFilter, fetchScoopData]);

  /** 튜브 선택 전 스텝 개수 (게임페이지 진입 ~ 가이드 클릭) - 튜브 필터 시 이 구간은 전체 데이터 사용 */
  const TUBE_PRE_CHOICE_STEP_COUNT = 4;

  const fetchTubeData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');
    const tubeType = mapTubeFilterToApi(tubeFilter);

    const baseParams: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      // 항상 tube_type=all 1회만 호출. 선택 안함 → 전체 합산, 특정 튜브 선택 → 같은 데이터에서 1~4번은 전체/5~10번은 해당 튜브만 프론트에서 필터.
      const response = await apiClient.get<TubeFishingApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/tube`,
        { params: { ...baseParams, tube_type: 'all' } },
      );
      const data = response.data ?? {};
      const rawDaily: TubeFishingDailyStat[] = data.dailyStats ?? [];
      const rawTotal: TubeFishingResult[] = Array.isArray(data.totalStats) ? data.totalStats : [];
      setTubeChoicePercentages(computeTubeChoicePercentages(rawTotal));
      setMoreGameTicketPercentages(computeMoreGameTicketPercentages(rawTotal));
      const allTotal = aggregateTubeResultsByEvent(rawTotal);
      let dailyStats: TubeFishingDailyStat[];
      let totalMap: Record<string, number>;

      if (tubeType !== 'all') {
        const filteredTotal = aggregateTubeResultsByEvent(
          rawTotal.filter((r) => (r.tube ?? '') === tubeType),
        );
        totalMap = {};
        TUBE_EVENT_ORDER.forEach(({ event }, index) => {
          totalMap[event] =
            index < TUBE_PRE_CHOICE_STEP_COUNT
              ? (allTotal[event] ?? 0)
              : (filteredTotal[event] ?? 0);
        });
        dailyStats = rawDaily.map((d) => {
          const allMap = aggregateTubeResultsByEvent(d.results);
          const filteredMap = aggregateTubeResultsByEvent(
            d.results.filter((r) => (r.tube ?? '') === tubeType),
          );
          const mergedMap: Record<string, number> = {};
          TUBE_EVENT_ORDER.forEach(({ event }, index) => {
            mergedMap[event] =
              index < TUBE_PRE_CHOICE_STEP_COUNT ? (allMap[event] ?? 0) : (filteredMap[event] ?? 0);
          });
          return {
            date: d.date,
            results: Object.entries(mergedMap).map(([event, count]) => ({ event, count })),
          };
        });
      } else {
        totalMap = allTotal;
        dailyStats = rawDaily;
      }

      const newConversionData: ConversionStep[] = TUBE_EVENT_ORDER.map(
        ({ label, event }, index) => {
          const count = totalMap[event] ?? 0;

          if (index === 0) {
            const percentage = roundRate(count > 0 ? 100 : 0);
            return { label, percentage, count };
          }

          const prevEvent = TUBE_EVENT_ORDER[index - 1]?.event;
          const prevCount = prevEvent ? (totalMap[prevEvent] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { label, percentage, count };
        },
      );

      const newRows: RouletteTubeFishingRow[] = dailyStats.map((daily, index) => {
        const eventMap = aggregateTubeResultsByEvent(daily.results);

        const firstEvent = TUBE_EVENT_ORDER[0]?.event;
        const firstDailyCount = firstEvent ? (eventMap[firstEvent] ?? 0) : 0;
        const lastEvent = TUBE_EVENT_ORDER[TUBE_EVENT_ORDER.length - 1]?.event;
        const lastCount = lastEvent ? (eventMap[lastEvent] ?? 0) : 0;
        const totalRate = roundRate(firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0);

        const steps = TUBE_EVENT_ORDER.map(({ event }, stepIndex) => {
          const count = eventMap[event] ?? 0;

          if (stepIndex === 0) {
            const rate = roundRate(count > 0 ? 100 : 0);
            return { count, rate };
          }

          const prevEvent = TUBE_EVENT_ORDER[stepIndex - 1]?.event;
          const prevCount = prevEvent ? (eventMap[prevEvent] ?? 0) : 0;
          const rate = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);

          return { count, rate };
        });

        return {
          id: index + 1,
          date: daily.date,
          totalCount: lastCount,
          totalRate,
          steps,
        };
      });

      setTubeConversionData(newConversionData);
      setTubeRows(newRows);
    } catch {
      setTubeConversionData(buildEmptyTubeConversionData());
      setTubeRows([]);
      setTubeChoicePercentages(null);
      setMoreGameTicketPercentages(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter, tubeFilter]);

  useEffect(() => {
    if (selectedContent === '튜브 낚시') {
      fetchTubeData();
    }
  }, [selectedContent, fetchTubeData]);

  const fetchFirstVisitData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      const response = await apiClient.get<FirstVisitApiResponse>(
        `/api/v1/analytics/pointhome/roulette/event/welcome`,
        { params },
      );
      setFirstVisitApiData(response.data);
    } catch {
      setFirstVisitApiData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, userFilter]);

  useEffect(() => {
    if (selectedContent === '첫 방문 보상') {
      fetchFirstVisitData();
    }
  }, [selectedContent, fetchFirstVisitData]);

  const clearCompareRows = useCallback(() => {
    setHomevisitCompareRows([]);
    setVideoAdCompareRows([]);
    setRandomAdCompareRows([]);
    setAdticketCompareRows([]);
    setTubeCompareRows([]);
    setScoopCompareRows([]);
    setRepairCompareRows([]);
    setPullupCompareRows([]);
    setFortuneCompareRows([]);
    setFirstVisitCompareRows([]);
  }, []);

  /** 기간 비교: 비교 기간으로 API 한 번 더 호출해 compareConversionData 설정 */
  const fetchCompareData = useCallback(async () => {
    if (
      !periodCompareStartDate ||
      !periodCompareEndDate ||
      periodCompareEndDate.isBefore(periodCompareStartDate)
    ) {
      setCompareConversionData(null);
      clearCompareRows();
      return;
    }
    if (selectedApp === null) return;

    const cmpStart = periodCompareStartDate.format('YYYY-MM-DD');
    const cmpEnd = periodCompareEndDate.format('YYYY-MM-DD');
    const baseParams = {
      start_date: cmpStart,
      end_date: cmpEnd,
      app_name: selectedApp === '앱 전체' ? 'all' : selectedApp,
      user_type: mapUserFilterToUserType(userFilter) ?? 'all',
    };

    try {
      if (selectedContent === '홈 방문하기') {
        const res = await apiClient.get<HomevisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/homevisit`,
          { params: baseParams },
        );
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});
        const data: ConversionStep[] = HOMEVISIT_EVENT_ORDER.map(({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevLabel = HOMEVISIT_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevLabel ? (totalMap[prevLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteHomeVisitRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[cur.eventLabel] = cur.eventCount;
            return acc;
          }, {});
          const firstEventLabel = HOMEVISIT_EVENT_ORDER[0]?.eventLabel;
          const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
          const lastEventLabel =
            HOMEVISIT_EVENT_ORDER[HOMEVISIT_EVENT_ORDER.length - 1]?.eventLabel;
          const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = HOMEVISIT_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
            const count = eventMap[eventLabel] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEventLabel = HOMEVISIT_EVENT_ORDER[stepIndex - 1]?.eventLabel;
            const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows(newRows);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '영상 광고 보기') {
        const res = await apiClient.get<HomevisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/longad`,
          { params: baseParams },
        );
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});
        const data: ConversionStep[] = LONGAD_EVENT_ORDER.map(({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevLabel = LONGAD_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevLabel ? (totalMap[prevLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteVideoAdRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[cur.eventLabel] = cur.eventCount;
            return acc;
          }, {});
          const firstEventLabel = LONGAD_EVENT_ORDER[0]?.eventLabel;
          const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
          const lastEventLabel = LONGAD_EVENT_ORDER[LONGAD_EVENT_ORDER.length - 1]?.eventLabel;
          const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = LONGAD_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
            const count = eventMap[eventLabel] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEventLabel = LONGAD_EVENT_ORDER[stepIndex - 1]?.eventLabel;
            const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows(newRows);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '랜덤 광고 보기') {
        const res = await apiClient.get<HomevisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/shortad`,
          { params: baseParams },
        );
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});
        const data: ConversionStep[] = SHORTAD_EVENT_ORDER.map(({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevLabel = SHORTAD_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevLabel ? (totalMap[prevLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteRandomAdRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[cur.eventLabel] = cur.eventCount;
            return acc;
          }, {});
          const firstEventLabel = SHORTAD_EVENT_ORDER[0]?.eventLabel;
          const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
          const lastEventLabel = SHORTAD_EVENT_ORDER[SHORTAD_EVENT_ORDER.length - 1]?.eventLabel;
          const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = SHORTAD_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
            const count = eventMap[eventLabel] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEventLabel = SHORTAD_EVENT_ORDER[stepIndex - 1]?.eventLabel;
            const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows(newRows);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '광고보고 티켓 얻기') {
        const res = await apiClient.get<AdticketApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/adticket`,
          { params: baseParams },
        );
        const totalMap = aggregateAdticketResultsByKey(res.data?.totalStats ?? []);
        const data: ConversionStep[] = ADTICKET_EVENT_ORDER.map((step, index) => {
          const count = totalMap[adticketResultKey(step)] ?? 0;
          if (index === 0)
            return { label: step.label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevCount = totalMap[adticketResultKey(ADTICKET_EVENT_ORDER[index - 1])] ?? 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label: step.label, percentage, count };
        });
        const dailyStats = res.data?.dailyStats ?? [];
        const newAdticketRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
          const eventMap = aggregateAdticketResultsByKey(daily.results);
          const firstCount = eventMap[adticketResultKey(ADTICKET_EVENT_ORDER[0])] ?? 0;
          const lastKey = adticketResultKey(ADTICKET_EVENT_ORDER[ADTICKET_EVENT_ORDER.length - 1]);
          const lastCount = eventMap[lastKey] ?? 0;
          const totalRate = roundRate(firstCount > 0 ? (lastCount / firstCount) * 100 : 0);
          const steps = ADTICKET_EVENT_ORDER.map((step, stepIndex) => {
            const count = eventMap[adticketResultKey(step)] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevCount = eventMap[adticketResultKey(ADTICKET_EVENT_ORDER[stepIndex - 1])] ?? 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows(newAdticketRows);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '뜰채 낚시') {
        const isBonusWon = timingFilter === '보너스 티켓 당첨';
        const res = await apiClient.get<TimingFishingApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/scoop`,
          { params: { ...baseParams, bonus_type: isBonusWon } },
        );
        const rawTotal = Array.isArray(res.data?.totalStats) ? res.data.totalStats : [];
        const data = buildConversionFromEventOrder(rawTotal, SCOOP_EVENT_ORDER);
        const dailyStats: TimingFishingDailyStat[] = res.data?.dailyStats ?? [];
        const newRows = buildRowsFromEventOrder<RouletteConversionStepsRow>(
          dailyStats,
          SCOOP_EVENT_ORDER,
        );
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows(newRows);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '통발 수리') {
        const res = await apiClient.get<HomevisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/repair`,
          { params: baseParams },
        );
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});
        const data: ConversionStep[] = REPAIR_EVENT_ORDER.map(({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevLabel = REPAIR_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevLabel ? (totalMap[prevLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[cur.eventLabel] = cur.eventCount;
            return acc;
          }, {});
          const firstEventLabel = REPAIR_EVENT_ORDER[0]?.eventLabel;
          const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
          const lastEventLabel = REPAIR_EVENT_ORDER[REPAIR_EVENT_ORDER.length - 1]?.eventLabel;
          const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = REPAIR_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
            const count = eventMap[eventLabel] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEventLabel = REPAIR_EVENT_ORDER[stepIndex - 1]?.eventLabel;
            const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows(newRows);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '통발 건지기') {
        const res = await apiClient.get<HomevisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/pullup`,
          { params: baseParams },
        );
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});
        const data: ConversionStep[] = PULLUP_EVENT_ORDER.map(({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevLabel = PULLUP_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevLabel ? (totalMap[prevLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[cur.eventLabel] = cur.eventCount;
            return acc;
          }, {});
          const firstEventLabel = PULLUP_EVENT_ORDER[0]?.eventLabel;
          const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
          const lastEventLabel = PULLUP_EVENT_ORDER[PULLUP_EVENT_ORDER.length - 1]?.eventLabel;
          const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = PULLUP_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
            const count = eventMap[eventLabel] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEventLabel = PULLUP_EVENT_ORDER[stepIndex - 1]?.eventLabel;
            const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows(newRows);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '낚시운세') {
        const res = await apiClient.get<HomevisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/fortune`,
          { params: baseParams },
        );
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.eventLabel] = cur.eventCount;
          return acc;
        }, {});
        const data: ConversionStep[] = FORTUNE_EVENT_ORDER.map(({ label, eventLabel }, index) => {
          const count = totalMap[eventLabel] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevLabel = FORTUNE_EVENT_ORDER[index - 1]?.eventLabel;
          const prevCount = prevLabel ? (totalMap[prevLabel] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[cur.eventLabel] = cur.eventCount;
            return acc;
          }, {});
          const firstEventLabel = FORTUNE_EVENT_ORDER[0]?.eventLabel;
          const firstDailyCount = firstEventLabel ? (eventMap[firstEventLabel] ?? 0) : 0;
          const lastEventLabel = FORTUNE_EVENT_ORDER[FORTUNE_EVENT_ORDER.length - 1]?.eventLabel;
          const lastCount = lastEventLabel ? (eventMap[lastEventLabel] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = FORTUNE_EVENT_ORDER.map(({ eventLabel }, stepIndex) => {
            const count = eventMap[eventLabel] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEventLabel = FORTUNE_EVENT_ORDER[stepIndex - 1]?.eventLabel;
            const prevCount = prevEventLabel ? (eventMap[prevEventLabel] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows(newRows);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      if (selectedContent === '튜브 낚시') {
        const res = await apiClient.get<TubeFishingApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/tube`,
          { params: { ...baseParams, tube_type: 'all' } },
        );
        const rawTotal = Array.isArray(res.data?.totalStats) ? res.data.totalStats : [];
        const totalMap = aggregateTubeResultsByEvent(rawTotal);
        const data: ConversionStep[] = TUBE_EVENT_ORDER.map(({ label, event }, index) => {
          const count = totalMap[event] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prevEvent = TUBE_EVENT_ORDER[index - 1]?.event;
          const prevCount = prevEvent ? (totalMap[prevEvent] ?? 0) : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const rawDaily: TubeFishingDailyStat[] = res.data?.dailyStats ?? [];
        const newRows: RouletteTubeFishingRow[] = rawDaily.map((daily, index) => {
          const eventMap = aggregateTubeResultsByEvent(daily.results);
          const firstEvent = TUBE_EVENT_ORDER[0]?.event;
          const firstDailyCount = firstEvent ? (eventMap[firstEvent] ?? 0) : 0;
          const lastEvent = TUBE_EVENT_ORDER[TUBE_EVENT_ORDER.length - 1]?.event;
          const lastCount = lastEvent ? (eventMap[lastEvent] ?? 0) : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = TUBE_EVENT_ORDER.map(({ event }, stepIndex) => {
            const count = eventMap[event] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prevEvent = TUBE_EVENT_ORDER[stepIndex - 1]?.event;
            const prevCount = prevEvent ? (eventMap[prevEvent] ?? 0) : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows(newRows);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows([]);
        setTubeCompareChoicePercentages(computeTubeChoicePercentages(rawTotal));
        setMoreGameTicketComparePercentages(computeMoreGameTicketPercentages(rawTotal));
        return;
      }
      if (selectedContent === '첫 방문 보상') {
        const res = await apiClient.get<FirstVisitApiResponse>(
          `/api/v1/analytics/pointhome/roulette/event/welcome`,
          { params: baseParams },
        );
        const order = FIRST_VISIT_EVENT_ORDER;
        if (!res.data) {
          setCompareConversionData(null);
          clearCompareRows();
          return;
        }
        const totalMap = res.data.totalStats.reduce<Record<string, number>>((acc, cur) => {
          acc[firstVisitStatKey(cur.event, cur.popup_type)] = cur.count;
          return acc;
        }, {});
        const data: ConversionStep[] = order.map(({ label, event, popupType }, index) => {
          const count = totalMap[firstVisitStatKey(event, popupType)] ?? 0;
          if (index === 0) return { label, percentage: roundRate(count > 0 ? 100 : 0), count };
          const prev = order[index - 1];
          const prevCount = prev
            ? (totalMap[firstVisitStatKey(prev.event, prev.popupType)] ?? 0)
            : 0;
          const percentage = roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0);
          return { label, percentage, count };
        });
        const dailyStats = res.data.dailyStats ?? [];
        const newRows: RouletteConversionStepsRow[] = dailyStats.map((daily, index) => {
          const eventMap = daily.results.reduce<Record<string, number>>((acc, cur) => {
            acc[firstVisitStatKey(cur.event, cur.popup_type)] = cur.count;
            return acc;
          }, {});
          const first = order[0];
          const firstDailyCount = first
            ? (eventMap[firstVisitStatKey(first.event, first.popupType)] ?? 0)
            : 0;
          const last = order[order.length - 1];
          const lastCount = last
            ? (eventMap[firstVisitStatKey(last.event, last.popupType)] ?? 0)
            : 0;
          const totalRate = roundRate(
            firstDailyCount > 0 ? (lastCount / firstDailyCount) * 100 : 0,
          );
          const steps = order.map(({ event, popupType }, stepIndex) => {
            const count = eventMap[firstVisitStatKey(event, popupType)] ?? 0;
            if (stepIndex === 0) return { count, rate: roundRate(count > 0 ? 100 : 0) };
            const prev = order[stepIndex - 1];
            const prevCount = prev
              ? (eventMap[firstVisitStatKey(prev.event, prev.popupType)] ?? 0)
              : 0;
            return { count, rate: roundRate(prevCount > 0 ? (count / prevCount) * 100 : 0) };
          });
          return { id: index + 1, date: daily.date, totalCount: lastCount, totalRate, steps };
        });
        setCompareConversionData(data);
        setHomevisitCompareRows([]);
        setVideoAdCompareRows([]);
        setRandomAdCompareRows([]);
        setAdticketCompareRows([]);
        setTubeCompareRows([]);
        setScoopCompareRows([]);
        setRepairCompareRows([]);
        setPullupCompareRows([]);
        setFortuneCompareRows([]);
        setFirstVisitCompareRows(newRows);
        setTubeCompareChoicePercentages(null);
        setMoreGameTicketComparePercentages(null);
        return;
      }
      setTubeCompareChoicePercentages(null);
      setMoreGameTicketComparePercentages(null);
      setCompareConversionData(null);
      clearCompareRows();
    } catch {
      setCompareConversionData(null);
      setTubeCompareChoicePercentages(null);
      setMoreGameTicketComparePercentages(null);
      clearCompareRows();
    }
  }, [
    periodCompareStartDate,
    periodCompareEndDate,
    selectedContent,
    selectedApp,
    userFilter,
    timingFilter,
    clearCompareRows,
  ]);

  useEffect(() => {
    if (!showPeriodCompare) {
      setCompareConversionData(null);
      setTubeCompareChoicePercentages(null);
      setMoreGameTicketComparePercentages(null);
      clearCompareRows();
      return;
    }
    if (
      !periodCompareStartDate ||
      !periodCompareEndDate ||
      periodCompareEndDate.isBefore(periodCompareStartDate)
    ) {
      setCompareConversionData(null);
      setTubeCompareChoicePercentages(null);
      setMoreGameTicketComparePercentages(null);
      clearCompareRows();
      return;
    }
    fetchCompareData();
  }, [
    showPeriodCompare,
    periodCompareStartDate,
    periodCompareEndDate,
    fetchCompareData,
    clearCompareRows,
  ]);

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', p: { xs: 2, md: 3 } }}>
      {/* 상단 필터 */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 180 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 160 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorUser value={userFilter} onChange={setUserFilter} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <RouletteChartDashboard
          kpiData={kpiData}
          selectedContent={selectedContent}
          onContentChange={setSelectedContent}
          contentAnchorEl={contentAnchorEl}
          onContentAnchor={setContentAnchorEl}
          showPeriodCompare={showPeriodCompare}
          onTogglePeriodCompare={() => setShowPeriodCompare((prev) => !prev)}
          periodCompareStartDate={periodCompareStartDate}
          periodCompareEndDate={periodCompareEndDate}
          onPeriodCompareStartChange={setPeriodCompareStartDate}
          onPeriodCompareEndChange={setPeriodCompareEndDate}
          mainStartDate={startDate}
          mainEndDate={endDate}
          tubeFilter={tubeFilter}
          onTubeFilterChange={setTubeFilter}
          timingFilter={timingFilter}
          onTimingFilterChange={setTimingFilter}
          homevisitConversionData={homevisitConversionData}
          homevisitRows={homevisitRows}
          videoAdConversionData={videoAdConversionData}
          videoAdRows={videoAdRows}
          randomAdConversionData={randomAdConversionData}
          randomAdRows={randomAdRows}
          adticketConversionData={adticketConversionData}
          adticketRows={adticketRows}
          tubeConversionData={tubeConversionData}
          tubeRows={tubeRows}
          tubeChoicePercentages={tubeChoicePercentages}
          moreGameTicketPercentages={moreGameTicketPercentages}
          scoopConversionData={scoopConversionData}
          scoopRows={scoopRows}
          repairConversionData={repairConversionData}
          repairRows={repairRows}
          pullupConversionData={pullupConversionData}
          pullupRows={pullupRows}
          fortuneConversionData={fortuneConversionData}
          fortuneRows={fortuneRows}
          firstVisitConversionData={firstVisitConversionData}
          firstVisitRows={firstVisitRows}
          compareConversionData={compareConversionData}
          tubeCompareChoicePercentages={tubeCompareChoicePercentages}
          moreGameTicketComparePercentages={moreGameTicketComparePercentages}
          homevisitCompareRows={homevisitCompareRows}
          videoAdCompareRows={videoAdCompareRows}
          randomAdCompareRows={randomAdCompareRows}
          adticketCompareRows={adticketCompareRows}
          tubeCompareRows={tubeCompareRows}
          scoopCompareRows={scoopCompareRows}
          repairCompareRows={repairCompareRows}
          pullupCompareRows={pullupCompareRows}
          fortuneCompareRows={fortuneCompareRows}
          firstVisitCompareRows={firstVisitCompareRows}
          selectedApp={selectedApp}
          appOptions={appOptions}
          onAppChange={setSelectedApp}
        />
      )}
    </Box>
  );
}
